import React, { useState, useRef } from "react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { useAccounting, useTeam } from "../../hooks/useFirebase";
import { AccountingService } from "../../firebase/databaseService";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const AccountingPage = () => {
  const { invoices, expenses, loading, createInvoice, addExpense, refresh } = useAccounting();
  const { teamMembers } = useTeam();

  // ========== TAB STATES ==========
  const [tab, setTab] = useState("invoices");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // ========== MODAL STATES ==========
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState("");

  // ========== PROJECT STATES ==========
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectFilter, setProjectFilter] = useState("all");

  // ========== FORM STATES ==========
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");

  const [invoiceForm, setInvoiceForm] = useState({
    clientName: "",
    clientAddress: "",
    clientGSTIN: "",
    clientState: "",
    projectName: "",
    items: [
      { description: "", quantity: 1, rate: "", gstRate: 18 }
    ],
    dueDate: "",
    notes: "",
    terms: "Payment due within 15 days from invoice date.",
  });

  const [expenseForm, setExpenseForm] = useState({
    category: "",
    amount: "",
    description: "",
    date: "",
    project: "",
  });

  const [paymentForm, setPaymentForm] = useState({
    invoiceId: "",
    amount: "",
    mode: "UPI",
    transactionId: "",
    paymentDate: "",
    notes: "",
  });

  // ========== COMPANY INFO ==========
  const companyInfo = {
    name: "SDtech Solutions",
    address: "BSF Colony Sai Town, Nainod, Indore, Madhya Pradesh 453112",
    phone: "+91 7000191398",
    email: "info@sdtechsolutions.com",
    website: "www.sdtechsolutions.com",
    gstin: "23AABCS1234F1Z5",
    pan: "AABCS1234F",
  };

  // ========== PDF REF ==========
  const invoiceRef = useRef(null);

  // ==========================================
  // INVOICE ITEM HANDLERS
  // ==========================================
  const addInvoiceItem = () => {
    setInvoiceForm((prev) => ({
      ...prev,
      items: [...prev.items, { description: "", quantity: 1, rate: "", gstRate: 18 }],
    }));
  };

  const removeInvoiceItem = (index) => {
    if (invoiceForm.items.length <= 1) return;
    setInvoiceForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const updateInvoiceItem = (index, field, value) => {
    setInvoiceForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  // ==========================================
  // CALCULATIONS
  // ==========================================
  const calculateTotals = (items) => {
    let subTotal = 0;
    let totalGST = 0;
    const itemDetails = items.map((item) => {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      const gstRate = parseFloat(item.gstRate) || 0;
      const amount = qty * rate;
      const gstAmount = (amount * gstRate) / 100;
      subTotal += amount;
      totalGST += gstAmount;
      return { ...item, amount, gstAmount, total: amount + gstAmount };
    });
    const grandTotal = subTotal + totalGST;
    const cgst = totalGST / 2;
    const sgst = totalGST / 2;
    return { itemDetails, subTotal, totalGST, cgst, sgst, grandTotal };
  };

  const invoiceTotals = calculateTotals(invoiceForm.items);

  // ==========================================
  // CREATE INVOICE
  // ==========================================
  const handleCreateInvoice = async () => {
    if (!invoiceForm.clientName) { setModalError("Client name required!"); return; }
    if (!invoiceForm.items.some((i) => i.description && i.rate)) {
      setModalError("Kam se kam ek item add karo!");
      return;
    }

    setModalLoading(true);
    setModalError("");

    try {
      const validItems = invoiceForm.items.filter((i) => i.description && i.rate);
      const totals = calculateTotals(validItems);

      const invoiceData = {
        clientName: invoiceForm.clientName,
        clientAddress: invoiceForm.clientAddress,
        clientGSTIN: invoiceForm.clientGSTIN,
        clientState: invoiceForm.clientState,
        projectName: invoiceForm.projectName,
        items: totals.itemDetails,
        subTotal: totals.subTotal,
        totalGST: totals.totalGST,
        cgst: totals.cgst,
        sgst: totals.sgst,
        grandTotal: totals.grandTotal,
        dueDate: invoiceForm.dueDate,
        notes: invoiceForm.notes,
        terms: invoiceForm.terms,
        companyInfo,
      };

      const result = await createInvoice(invoiceData);
      console.log("✅ Invoice created:", result.invoiceNo);

      setModalSuccess(`✅ Invoice ${result.invoiceNo} created!`);
      refresh();
      setTimeout(() => {
        setShowInvoiceModal(false);
        setModalSuccess("");
        resetInvoiceForm();
      }, 1500);
    } catch (error) {
      setModalError("Failed: " + error.message);
    } finally {
      setModalLoading(false);
    }
  };

  const resetInvoiceForm = () => {
    setInvoiceForm({
      clientName: "", clientAddress: "", clientGSTIN: "", clientState: "",
      projectName: "",
      items: [{ description: "", quantity: 1, rate: "", gstRate: 18 }],
      dueDate: "", notes: "",
      terms: "Payment due within 15 days from invoice date.",
    });
  };

  // ==========================================
  // DOWNLOAD PDF
  // ==========================================
  const handleDownloadPDF = async (invoice) => {
    if (!invoiceRef.current) return;

    try {
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`Invoice-${invoice.invoiceNo}.pdf`);
    } catch (error) {
      console.error("PDF Error:", error);
      alert("PDF download failed! Try again.");
    }
  };

  // ==========================================
  // EXPENSE
  // ==========================================
  const handleAddExpense = async () => {
    if (!expenseForm.category || !expenseForm.amount) {
      setModalError("Category & Amount required!");
      return;
    }
    setModalLoading(true);
    try {
      await addExpense(expenseForm);
      setModalSuccess("✅ Expense added!");
      setExpenseForm({ category: "", amount: "", description: "", date: "", project: "" });
      refresh();
      setTimeout(() => { setShowExpenseModal(false); setModalSuccess(""); }, 1500);
    } catch (error) {
      setModalError(error.message);
    } finally {
      setModalLoading(false);
    }
  };

  // ==========================================
  // MARK PAID
  // ==========================================
  const openMarkPaidModal = (invoice) => {
    setPaymentForm({
      invoiceId: invoice.id,
      amount: invoice.grandTotal?.toString() || invoice.total?.toString() || "",
      mode: "UPI",
      transactionId: "",
      paymentDate: new Date().toISOString().split("T")[0],
      notes: "",
    });
    setShowMarkPaidModal(true);
  };

  const handleMarkPaid = async () => {
    if (!paymentForm.amount) { setModalError("Amount required!"); return; }
    setModalLoading(true);
    try {
      await AccountingService.markInvoicePaid(paymentForm.invoiceId, {
        amount: paymentForm.amount,
        mode: paymentForm.mode,
        transactionId: paymentForm.transactionId,
        paymentDate: paymentForm.paymentDate,
        notes: paymentForm.notes,
      });
      setModalSuccess("✅ Invoice marked as paid!");
      refresh();
      setTimeout(() => { setShowMarkPaidModal(false); setModalSuccess(""); }, 1500);
    } catch (error) {
      setModalError(error.message);
    } finally {
      setModalLoading(false);
    }
  };

  // ==========================================
  // DELETE
  // ==========================================
  const openDeleteModal = (item, type) => {
    setDeleteTarget(item);
    setDeleteType(type);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setModalLoading(true);
    try {
      if (deleteType === "invoice") await AccountingService.deleteInvoice(deleteTarget.id);
      else await AccountingService.deleteExpense(deleteTarget.id);
      setShowDeleteModal(false);
      refresh();
    } catch (error) {
      setModalError(error.message);
    } finally {
      setModalLoading(false);
    }
  };

  // ==========================================
  // FILTERED DATA
  // ==========================================
  const filteredInvoices = invoices.filter((inv) => {
    const matchesSearch =
      inv.invoiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.projectName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || inv.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredExpenses = expenses.filter((exp) => {
    return (
      exp.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exp.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // ==========================================
  // STATS
  // ==========================================
  const totalRevenue = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + parseFloat(i.grandTotal || i.total || 0), 0);
  const totalPending = invoices.filter((i) => i.status === "pending").reduce((s, i) => s + parseFloat(i.grandTotal || i.total || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const netProfit = totalRevenue - totalExpenses;

  // ==========================================
  // INVOICE COLUMNS
  // ==========================================
  const invoiceColumns = [
    {
      header: "Invoice No",
      render: (r) => (
        <button className="btn btn-ghost-blue" style={{ fontSize: 12 }} onClick={() => { setPaymentForm({ ...paymentForm, invoiceId: r.id }); setShowInvoicePreview(true); setPreviewInvoice(r); }}>
          {r.invoiceNo}
        </button>
      ),
    },
    {
      header: "Client",
      render: (r) => (
        <div>
          <div className="font-bold text-14">{r.clientName}</div>
          <div className="text-12 text-gray-400">{r.projectName || "—"}</div>
        </div>
      ),
    },
    {
      header: "Sub Total",
      render: (r) => <span>₹{parseFloat(r.subTotal || 0).toLocaleString("en-IN")}</span>,
    },
    {
      header: "GST",
      render: (r) => <span>₹{parseFloat(r.totalGST || 0).toLocaleString("en-IN")}</span>,
    },
    {
      header: "Total",
      render: (r) => (
        <span className="font-bold text-blue-600">
          ₹{parseFloat(r.grandTotal || r.total || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      header: "Status",
      render: (r) => <span className={`status-badge badge-${r.status}`}>{r.status}</span>,
    },
    {
      header: "Due Date",
      render: (r) => <span className="text-12 text-gray-400">{r.dueDate || "—"}</span>,
    },
  ];

  // ========== PREVIEW INVOICE STATE ==========
  const [previewInvoice, setPreviewInvoice] = useState(null);

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div>
      <PageHeader title="Accounting & Invoicing" breadcrumbs={["Admin", "Accounting"]} />

      {/* ========== FINANCIAL STATS ========== */}
      <div className="admin-grid admin-grid-4" style={{ marginTop: 24, marginBottom: 24 }}>
        {[
          { label: "Total Revenue", value: `₹${totalRevenue.toLocaleString("en-IN")}`, icon: "💰", cls: "qs-green" },
          { label: "Pending Payment", value: `₹${totalPending.toLocaleString("en-IN")}`, icon: "⏰", cls: "qs-orange" },
          { label: "Total Expenses", value: `₹${totalExpenses.toLocaleString("en-IN")}`, icon: "📉", cls: "qs-red" },
          { label: "Net Profit", value: `₹${netProfit.toLocaleString("en-IN")}`, icon: "📈", cls: "qs-blue" },
        ].map((s, i) => (
          <div key={i} className={`quick-stat ${s.cls}`}>
            <div className="quick-stat-icon">{s.icon}</div>
            <div className="quick-stat-info">
              <div className="quick-stat-label">{s.label}</div>
              <div className="quick-stat-value">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ========== TABS ========== */}
      <div className="admin-tabs" style={{ marginBottom: 24 }}>
        {[
          { id: "invoices", label: "📄 Invoices", count: invoices.length },
          { id: "expenses", label: "📝 Expenses", count: expenses.length },
          { id: "reports", label: "📊 Reports", count: null },
        ].map((t) => (
          <button key={t.id} className={`tab-btn ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
            {t.label} {t.count !== null && <span className="tab-badge">{t.count}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="admin-loading"><div className="spinner-wrap"><div className="spinner" /><p className="spinner-text">Loading...</p></div></div>
      ) : (
        <>
          {/* ==========================================
              TAB: INVOICES
          ========================================== */}
          {tab === "invoices" && (
            <div>
              {/* Toolbar */}
              <div className="admin-toolbar" style={{ marginBottom: 24 }}>
                <div className="toolbar-left">
                  <div className="admin-form-group" style={{ minWidth: 200 }}>
                    <input type="text" placeholder="🔍 Search invoice, client..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </div>
                  <div className="admin-form-group">
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                    </select>
                  </div>
                </div>
                <div className="toolbar-right">
                  <button className="btn btn-primary" onClick={() => { setModalError(""); setShowInvoiceModal(true); }}>
                    + Create Invoice
                  </button>
                </div>
              </div>

              {/* Invoices Table */}
              <DataTable columns={invoiceColumns} data={filteredInvoices} actions={false} />

              {/* Quick Actions per Invoice */}
              <div className="admin-toolbar" style={{ marginTop: 24 }}>
                <div className="toolbar-left">
                  <h3 className="font-bold text-14">Quick Actions</h3>
                </div>
                <div className="toolbar-right" style={{ flexWrap: "wrap" }}>
                  {filteredInvoices.filter((i) => i.status !== "paid").map((inv) => (
                    <div key={inv.id} style={{ display: "flex", gap: 8, padding: "4px 8px", backgroundColor: "#f9fafb", borderRadius: 8 }}>
                      <span className="font-bold text-12">{inv.invoiceNo}</span>
                      <button className="btn btn-ghost-green" style={{ fontSize: 11 }} onClick={() => openMarkPaidModal(inv)}>💰 Mark Paid</button>
                      <button className="btn btn-ghost-red" style={{ fontSize: 11 }} onClick={() => openDeleteModal(inv, "invoice")}>🗑</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ==========================================
              TAB: EXPENSES
          ========================================== */}
          {tab === "expenses" && (
            <div>
              <div className="admin-toolbar" style={{ marginBottom: 24 }}>
                <div className="toolbar-left">
                  <h3 className="font-bold text-20 text-gray-900">📝 Expenses</h3>
                </div>
                <div className="toolbar-right">
                  <button className="btn btn-primary" onClick={() => { setModalError(""); setShowExpenseModal(true); }}>
                    + Add Expense
                  </button>
                </div>
              </div>

              <div className="admin-grid admin-grid-3">
                {filteredExpenses.length === 0 ? (
                  <div className="text-center text-gray-400" style={{ gridColumn: "span 3", backgroundColor: "white", borderRadius: 12, padding: 48 }}>
                    <p className="text-24 mb-2">📝</p><p>No expenses yet.</p>
                  </div>
                ) : (
                  filteredExpenses.map((exp) => (
                    <div key={exp.id} className="expense-card">
                      <div className="expense-header">
                        <span className="expense-category">{exp.category}</span>
                        <span className="expense-date">{exp.date}</span>
                      </div>
                      <div className="expense-amount">₹{parseFloat(exp.amount).toLocaleString("en-IN")}</div>
                      <div className="expense-desc">{exp.description}</div>
                      <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
                        <button className="btn btn-ghost-red" style={{ fontSize: 11 }} onClick={() => openDeleteModal(exp, "expense")}>🗑 Delete</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ==========================================
              TAB: REPORTS
          ========================================== */}
          {tab === "reports" && (
            <div className="admin-grid admin-grid-2">
              <div className="data-table-wrapper">
                <div className="data-table-toolbar">
                  <div className="toolbar-title"><h3>📊 Monthly Revenue vs Expenses</h3></div>
                </div>
                <div style={{ padding: 20 }}>
                  <div className="space-y-3">
                    {["2026-01", "2025-12", "2025-11", "2025-10"].map((month) => {
                      const monthInvoices = invoices.filter((i) => (i.createdAt || "").startsWith(month));
                      const monthExpenses = expenses.filter((e) => (e.date || "").startsWith(month));
                      const monthRev = monthInvoices.filter((i) => i.status === "paid").reduce((s, i) => s + parseFloat(i.grandTotal || i.total || 0), 0);
                      const monthExp = monthExpenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
                      return (
                        <div key={month} style={{ padding: 12, backgroundColor: "#f9fafb", borderRadius: 8, display: "flex", justifyContent: "space-between" }}>
                          <span className="font-bold text-14">{new Date(month).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</span>
                          <div style={{ display: "flex", gap: 24 }}>
                            <div className="text-right">
                              <div className="text-12 text-gray-400">Revenue</div>
                              <div className="font-bold text-green-600">₹{monthRev.toLocaleString("en-IN")}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-12 text-gray-400">Expenses</div>
                              <div className="font-bold text-red-600">₹{monthExp.toLocaleString("en-IN")}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-12 text-gray-400">Profit</div>
                              <div className="font-bold text-blue-600">₹{(monthRev - monthExp).toLocaleString("en-IN")}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="data-table-wrapper">
                <div className="data-table-toolbar">
                  <div className="toolbar-title"><h3>🗂 Invoices by Status</h3></div>
                </div>
                <div style={{ padding: 20 }}>
                  <div className="funnel-step">
                    <div className="funnel-label"><span className="step-name">Total Invoices</span><span className="step-value">{invoices.length}</span></div>
                    <div className="funnel-bar"><div className="funnel-bar-fill color-blue" style={{ width: "100%" }}><span className="fill-percent">100%</span></div></div>
                  </div>
                  <div className="funnel-step">
                    <div className="funnel-label"><span className="step-name">Paid</span><span className="step-value">{invoices.filter((i) => i.status === "paid").length}</span></div>
                    <div className="funnel-bar"><div className="funnel-bar-fill color-green" style={{ width: `${invoices.length ? (invoices.filter((i) => i.status === "paid").length / invoices.length) * 100 : 0}%` }}><span className="fill-percent">{invoices.length ? Math.round((invoices.filter((i) => i.status === "paid").length / invoices.length) * 100) : 0}%</span></div></div>
                  </div>
                  <div className="funnel-step">
                    <div className="funnel-label"><span className="step-name">Pending</span><span className="step-value">{invoices.filter((i) => i.status === "pending").length}</span></div>
                    <div className="funnel-bar"><div className="funnel-bar-fill color-orange" style={{ width: `${invoices.length ? (invoices.filter((i) => i.status === "pending").length / invoices.length) * 100 : 0}%` }}><span className="fill-percent">{invoices.length ? Math.round((invoices.filter((i) => i.status === "pending").length / invoices.length) * 100) : 0}%</span></div></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ==========================================
          MODAL: CREATE INVOICE
      ========================================== */}
      {showInvoiceModal && (
        <div className="modal-overlay" onClick={() => setShowInvoiceModal(false)}>
          <div className="modal-box modal-lg" onClick={(e) => e.stopPropagation()} style={{ maxHeight: "90vh", overflowY: "auto" }}>
            <div className="modal-header">
              <h3>📄 Create Invoice</h3>
              <button className="modal-close-btn" onClick={() => setShowInvoiceModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {modalError && <div style={{ marginBottom: 12, padding: 12, backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}><p style={{ color: "#991b1b", fontSize: 14, margin: 0 }}>❌ {modalError}</p></div>}

              {/* Company Info */}
              <div style={{ padding: 12, backgroundColor: "#f0f9ff", border: "1px solid #bfdbfe", borderRadius: 8, marginBottom: 16 }}>
                <h4 style={{ margin: "0 0 4px", fontSize: 13, color: "#1e40af" }}>🏢 From:</h4>
                <p style={{ margin: 0, fontSize: 12, color: "#374151" }}>
                  <strong>{companyInfo.name}</strong> • GSTIN: {companyInfo.gstin}<br />
                  {companyInfo.address}<br />
                  {companyInfo.phone} • {companyInfo.email}
                </p>
              </div>

              {/* Client Details */}
              <h4 style={{ fontSize: 14, fontWeight: 700, color: "#374151", marginBottom: 12 }}>👤 Bill To:</h4>
              <div className="form-row">
                <div className="admin-form-group">
                  <label>Client Name *</label>
                  <input type="text" value={invoiceForm.clientName} onChange={(e) => setInvoiceForm({ ...invoiceForm, clientName: e.target.value })} placeholder="Client company name" disabled={modalLoading} />
                </div>
                <div className="admin-form-group">
                  <label>Project Name</label>
                  <input type="text" value={invoiceForm.projectName} onChange={(e) => setInvoiceForm({ ...invoiceForm, projectName: e.target.value })} placeholder="e.g. Website Development" disabled={modalLoading} />
                </div>
              </div>
              <div className="form-row">
                <div className="admin-form-group">
                  <label>Client Address</label>
                  <input type="text" value={invoiceForm.clientAddress} onChange={(e) => setInvoiceForm({ ...invoiceForm, clientAddress: e.target.value })} placeholder="Client address" disabled={modalLoading} />
                </div>
                <div className="admin-form-group">
                  <label>Client GSTIN</label>
                  <input type="text" value={invoiceForm.clientGSTIN} onChange={(e) => setInvoiceForm({ ...invoiceForm, clientGSTIN: e.target.value })} placeholder="Client GSTIN" disabled={modalLoading} />
                </div>
              </div>
              <div className="form-row">
                <div className="admin-form-group">
                  <label>Due Date</label>
                  <input type="date" value={invoiceForm.dueDate} onChange={(e) => setInvoiceForm({ ...invoiceForm, dueDate: e.target.value })} disabled={modalLoading} />
                </div>
                <div className="admin-form-group">
                  <label>Terms</label>
                  <input type="text" value={invoiceForm.terms} onChange={(e) => setInvoiceForm({ ...invoiceForm, terms: e.target.value })} disabled={modalLoading} />
                </div>
              </div>

              {/* Line Items */}
              <h4 style={{ fontSize: 14, fontWeight: 700, color: "#374151", marginTop: 16, marginBottom: 12 }}>📦 Line Items</h4>

              <div style={{ marginBottom: 12 }}>
                <table className="data-table" style={{ width: "100%", fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ width: "35%" }}>Description *</th>
                      <th style={{ width: "10%" }}>Qty</th>
                      <th style={{ width: "15%" }}>Rate (₹) *</th>
                      <th style={{ width: "10%" }}>GST %</th>
                      <th style={{ width: "15%" }}>Amount</th>
                      <th style={{ width: "5%" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceForm.items.map((item, idx) => (
                      <tr key={idx}>
                        <td>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateInvoiceItem(idx, "description", e.target.value)}
                            placeholder="Service/item description"
                            disabled={modalLoading}
                            style={{ width: "100%", padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12 }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateInvoiceItem(idx, "quantity", e.target.value)}
                            min="1"
                            disabled={modalLoading}
                            style={{ width: "100%", padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12 }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={item.rate}
                            onChange={(e) => updateInvoiceItem(idx, "rate", e.target.value)}
                            placeholder="0"
                            disabled={modalLoading}
                            style={{ width: "100%", padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12 }}
                          />
                        </td>
                        <td>
                          <select
                            value={item.gstRate}
                            onChange={(e) => updateInvoiceItem(idx, "gstRate", e.target.value)}
                            disabled={modalLoading}
                            style={{ width: "100%", padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12 }}
                          >
                            <option value={0}>0%</option>
                            <option value={5}>5%</option>
                            <option value={12}>12%</option>
                            <option value={18}>18%</option>
                            <option value={28}>28%</option>
                          </select>
                        </td>
                        <td style={{ fontWeight: 700, color: "#2563eb" }}>
                          ₹{(((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)) + (((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)) * (parseFloat(item.gstRate) || 0)) / 100).toLocaleString("en-IN")}
                        </td>
                        <td>
                          {invoiceForm.items.length > 1 && (
                            <button
                              onClick={() => removeInvoiceItem(idx)}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 16 }}
                              disabled={modalLoading}
                            >
                              ✕
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button className="btn btn-ghost-blue" style={{ marginTop: 8 }} onClick={addInvoiceItem} disabled={modalLoading}>
                  + Add Item
                </button>
              </div>

              {/* Totals */}
              <div style={{ marginLeft: "auto", width: 300, padding: 16, backgroundColor: "#f9fafb", borderRadius: 8 }}>
                {[
                  { label: "Sub Total", value: `₹${invoiceTotals.subTotal.toLocaleString("en-IN")}` },
                  { label: "CGST", value: `₹${invoiceTotals.cgst.toLocaleString("en-IN")}` },
                  { label: "SGST", value: `₹${invoiceTotals.sgst.toLocaleString("en-IN")}` },
                  { label: "Grand Total", value: `₹${invoiceTotals.grandTotal.toLocaleString("en-IN")}`, bold: true },
                ].map((row, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderTop: row.bold ? "1px solid #e5e7eb" : "none" }}>
                    <span style={{ fontSize: row.bold ? 14 : 12, fontWeight: row.bold ? 700 : 400, color: row.bold ? "#111827" : "#6b7280" }}>{row.label}</span>
                    <span style={{ fontSize: row.bold ? 18 : 12, fontWeight: 700, color: row.bold ? "#2563eb" : "#374151" }}>{row.value}</span>
                  </div>
                ))}
              </div>

              <div className="admin-form-group" style={{ marginTop: 16 }}>
                <label>Notes</label>
                <textarea value={invoiceForm.notes} onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })} placeholder="Additional notes..." rows="2" disabled={modalLoading} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowInvoiceModal(false)} disabled={modalLoading}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreateInvoice} disabled={modalLoading}>
                {modalLoading ? "⏳ Creating..." : "📄 Create Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          MODAL: INVOICE PREVIEW (Professional Design)
      ========================================== */}
      {showInvoicePreview && previewInvoice && (
        <div className="modal-overlay" onClick={() => setShowInvoicePreview(false)} style={{ overflow: "auto", alignItems: "flex-start", paddingTop: 40 }}>
          <div style={{ width: "100%", maxWidth: 800 }} onClick={(e) => e.stopPropagation()}>
            {/* Download Button */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
              <button className="btn btn-primary" onClick={() => handleDownloadPDF(previewInvoice)}>⬇️ Download PDF</button>
              <button className="btn btn-outline" onClick={() => setShowInvoicePreview(false)}>✕ Close</button>
            </div>

            {/* Professional Invoice Design */}
            <div ref={invoiceRef} style={{ backgroundColor: "white", padding: 40, borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", fontFamily: "Inter, Arial, sans-serif" }}>
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "3px solid #2563eb", paddingBottom: 20, marginBottom: 24 }}>
                <div>
                  <h1 style={{ fontSize: 28, fontWeight: 800, color: "#2563eb", margin: 0 }}>SDtech Solutions</h1>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 0" }}>
                    {companyInfo.address}<br />
                    Phone: {companyInfo.phone} | Email: {companyInfo.email}<br />
                    GSTIN: {companyInfo.gstin} | PAN: {companyInfo.pan}
                  </p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <h2 style={{ fontSize: 36, fontWeight: 800, color: "#2563eb", margin: 0 }}>TAX INVOICE</h2>
                  <p style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: "8px 0 0" }}>
                    {previewInvoice.invoiceNo}
                  </p>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 0" }}>
                    Date: {new Date().toLocaleDateString("en-IN")}
                  </p>
                  {previewInvoice.dueDate && (
                    <p style={{ fontSize: 12, color: "#6b7280", margin: "2px 0 0" }}>
                      Due: {previewInvoice.dueDate}
                    </p>
                  )}
                </div>
              </div>

              {/* Client Info */}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
                <div style={{ backgroundColor: "#f9fafb", padding: 16, borderRadius: 8, flex: 1, marginRight: 16 }}>
                  <h4 style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", margin: "0 0 8px" }}>Bill To</h4>
                  <p style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: "0 0 4px" }}>
                    {previewInvoice.clientName}
                  </p>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                    {previewInvoice.clientAddress}
                  </p>
                  {previewInvoice.clientGSTIN && (
                    <p style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 0" }}>
                      GSTIN: {previewInvoice.clientGSTIN}
                    </p>
                  )}
                </div>
                <div style={{ backgroundColor: "#f9fafb", padding: 16, borderRadius: 8, width: 250 }}>
                  <h4 style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", margin: "0 0 8px" }}>Invoice Details</h4>
                  <p style={{ fontSize: 12, margin: "0 0 2px" }}>
                    <strong>Invoice #:</strong> {previewInvoice.invoiceNo}
                  </p>
                  <p style={{ fontSize: 12, margin: "0 0 2px" }}>
                    <strong>Project:</strong> {previewInvoice.projectName || "—"}
                  </p>
                  <p style={{ fontSize: 12, margin: "0 0 2px" }}>
                    <strong>Terms:</strong> {previewInvoice.terms || "—"}
                  </p>
                  <p style={{ fontSize: 12, margin: 0 }}>
                    <strong>Status:</strong>{" "}
                    <span style={{ color: previewInvoice.status === "paid" ? "#16a34a" : "#ea580c", fontWeight: 700 }}>
                      {previewInvoice.status?.toUpperCase()}
                    </span>
                  </p>
                </div>
              </div>

              {/* Items Table */}
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 24 }}>
                <thead>
                  <tr style={{ backgroundColor: "#2563eb" }}>
                    {["#", "Description", "Qty", "Rate", "GST %", "Amount"].map((h) => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "white", fontSize: 12, fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(previewInvoice.items || []).map((item, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", backgroundColor: i % 2 === 0 ? "white" : "#f9fafb" }}>
                      <td style={{ padding: "10px 12px", fontSize: 12, color: "#6b7280" }}>{i + 1}</td>
                      <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600 }}>{item.description}</td>
                      <td style={{ padding: "10px 12px", fontSize: 12 }}>{item.quantity}</td>
                      <td style={{ padding: "10px 12px", fontSize: 12 }}>₹{parseFloat(item.rate).toLocaleString("en-IN")}</td>
                      <td style={{ padding: "10px 12px", fontSize: 12 }}>{item.gstRate}%</td>
                      <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#2563eb" }}>
                        ₹{parseFloat(item.amount || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{ width: 300 }}>
                  {[
                    { label: "Sub Total", value: `₹${parseFloat(previewInvoice.subTotal || 0).toLocaleString("en-IN")}`, color: "#6b7280" },
                    { label: `CGST`, value: `₹${parseFloat(previewInvoice.cgst || 0).toLocaleString("en-IN")}`, color: "#6b7280" },
                    { label: `SGST`, value: `₹${parseFloat(previewInvoice.sgst || 0).toLocaleString("en-IN")}`, color: "#6b7280" },
                    { label: "GRAND TOTAL", value: `₹${parseFloat(previewInvoice.grandTotal || previewInvoice.total || 0).toLocaleString("en-IN")}`, color: "#2563eb", bold: true },
                  ].map((row, i) => (
                    <div key={i} style={{
                      display: "flex", justifyContent: "space-between",
                      padding: row.bold ? "10px 0" : "4px 0",
                      borderTop: row.bold ? "2px solid #2563eb" : "none",
                      marginTop: row.bold ? 4 : 0
                    }}>
                      <span style={{ fontSize: row.bold ? 14 : 12, fontWeight: row.bold ? 800 : 400, color: row.color }}>{row.label}</span>
                      <span style={{ fontSize: row.bold ? 20 : 12, fontWeight: 700, color: row.color }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes & Terms */}
              {previewInvoice.notes && (
                <div style={{ marginTop: 24, padding: 12, backgroundColor: "#f9fafb", borderRadius: 8 }}>
                  <strong style={{ fontSize: 12 }}>Notes:</strong>
                  <p style={{ fontSize: 12, margin: "4px 0 0", color: "#374151" }}>{previewInvoice.notes}</p>
                </div>
              )}

              {/* Footer */}
              <div style={{ marginTop: 32, borderTop: "1px solid #e5e7eb", paddingTop: 16, textAlign: "center" }}>
                <p style={{ fontSize: 11, color: "#9ca3af", margin: 0 }}>
                  This is a computer generated invoice from <strong style={{ color: "#2563eb" }}>SDtech Solutions</strong>.
                </p>
                <p style={{ fontSize: 11, color: "#9ca3af", margin: "4px 0 0" }}>
                  {previewInvoice.terms}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          MODAL: ADD EXPENSE
      ========================================== */}
      {showExpenseModal && (
        <div className="modal-overlay" onClick={() => setShowExpenseModal(false)}>
          <div className="modal-box modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📝 Add Expense</h3>
              <button className="modal-close-btn" onClick={() => setShowExpenseModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {modalError && <div style={{ marginBottom: 12, padding: 12, backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}><p style={{ color: "#991b1b", fontSize: 14, margin: 0 }}>❌ {modalError}</p></div>}
              {modalSuccess && <div style={{ marginBottom: 12, padding: 12, backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8 }}><p style={{ color: "#166534", fontSize: 14, margin: 0 }}>{modalSuccess}</p></div>}

              <div className="admin-form-group" style={{ marginBottom: 16 }}>
                <label>Category *</label>
                <select value={expenseForm.category} onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })} disabled={modalLoading}>
                  <option value="">Select Category</option>
                  <option>Salaries</option><option>Office Rent</option><option>Marketing</option><option>Software Tools</option><option>Internet & Phone</option><option>Travel</option><option>Utilities</option><option>Other</option>
                </select>
              </div>
              <div className="form-row">
                <div className="admin-form-group">
                  <label>Amount *</label>
                  <input type="number" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} placeholder="0" disabled={modalLoading} />
                </div>
                <div className="admin-form-group">
                  <label>Date</label>
                  <input type="date" value={expenseForm.date} onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })} disabled={modalLoading} />
                </div>
              </div>
              <div className="admin-form-group" style={{ marginTop: 16 }}>
                <label>Description</label>
                <input type="text" value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} placeholder="Expense details" disabled={modalLoading} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowExpenseModal(false)} disabled={modalLoading}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddExpense} disabled={modalLoading}>
                {modalLoading ? "⏳ Saving..." : "📝 Save Expense"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          MODAL: MARK PAID
      ========================================== */}
      {showMarkPaidModal && (
        <div className="modal-overlay" onClick={() => setShowMarkPaidModal(false)}>
          <div className="modal-box modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💰 Mark Invoice as Paid</h3>
              <button className="modal-close-btn" onClick={() => setShowMarkPaidModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {modalError && <div style={{ marginBottom: 12, padding: 12, backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}><p style={{ color: "#991b1b", fontSize: 14, margin: 0 }}>❌ {modalError}</p></div>}

              <div className="admin-form-group" style={{ marginBottom: 16 }}>
                <label>Amount *</label>
                <input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} disabled={modalLoading} />
              </div>
              <div className="form-row">
                <div className="admin-form-group">
                  <label>Payment Mode</label>
                  <select value={paymentForm.mode} onChange={(e) => setPaymentForm({ ...paymentForm, mode: e.target.value })} disabled={modalLoading}>
                    <option>UPI</option><option>Bank Transfer</option><option>Cheque</option><option>Cash</option><option>Card</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>Payment Date</label>
                  <input type="date" value={paymentForm.paymentDate} onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })} disabled={modalLoading} />
                </div>
              </div>
              <div className="admin-form-group" style={{ marginTop: 16 }}>
                <label>Transaction ID</label>
                <input type="text" value={paymentForm.transactionId} onChange={(e) => setPaymentForm({ ...paymentForm, transactionId: e.target.value })} placeholder="UPI Ref / Transaction ID" disabled={modalLoading} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowMarkPaidModal(false)} disabled={modalLoading}>Cancel</button>
              <button className="btn btn-success" onClick={handleMarkPaid} disabled={modalLoading}>
                {modalLoading ? "⏳ Processing..." : "💰 Mark as Paid"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          MODAL: DELETE
      ========================================== */}
      {showDeleteModal && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-box modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: "1px solid #fecaca" }}>
              <h3 style={{ color: "#dc2626" }}>🗑️ Delete</h3>
              <button className="modal-close-btn" onClick={() => setShowDeleteModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
              <h4 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 8px" }}>Are you sure?</h4>
              <p style={{ fontSize: 14, color: "#6b7280" }}>
                <strong style={{ color: "#dc2626" }}>{deleteTarget.invoiceNo || deleteTarget.category}</strong> delete ho jayega.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDeleteModal(false)} disabled={modalLoading}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={modalLoading}>
                {modalLoading ? "⏳ Deleting..." : "🗑️ Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountingPage;